import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Main, Styled } from './style';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type WooCommerceProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type WooResource = 'customer' | 'order' | 'product';

function isWooResource(value: unknown): value is WooResource {
   return value === 'customer' || value === 'order' || value === 'product';
}

const resourceOptionsBase: selectDataType[] = [
   { id: 1, name: 'Order', refId: 'order' },
   { id: 2, name: 'Product', refId: 'product' },
   { id: 3, name: 'Customer', refId: 'customer' }
];

const requestOptionsByResource: Record<WooResource, selectDataType[]> = {
   order: [
      { id: 1, name: 'Create Order', refId: 'createOrder' },
      { id: 2, name: 'Create Orders', refId: 'createOrders' },
      { id: 3, name: 'Get Order', refId: 'getOrder' },
      { id: 4, name: 'Get Orders', refId: 'getOrders' },
      { id: 5, name: 'Update Order', refId: 'updateOrder' },
      { id: 6, name: 'Delete Order', refId: 'deleteOrder' },
      { id: 7, name: 'Order Mark As Paid', refId: 'orderMarkAsPaid' },
      { id: 12, name: 'Refund Create', refId: 'refundCreate' },
      // { id: 8, name: 'Return Create', refId: 'returnCreate' },
      // { id: 9, name: 'Return Approve Request', refId: 'returnApproveRequest' },
      // { id: 10, name: 'Return Decline Request', refId: 'returnDeclineRequest' },
      // { id: 11, name: 'Return Process', refId: 'returnProcess' },
      // { id: 13, name: 'Inventory Restock Decision', refId: 'inventoryRestockDecision' },
      // { id: 14, name: 'Exchange Action', refId: 'exchangeAction' }
   ],
   product: [
      { id: 1, name: 'Create Product', refId: 'createProduct' },
      { id: 2, name: 'Create Products', refId: 'createProducts' },
      { id: 3, name: 'Get Product', refId: 'getProduct' },
      { id: 4, name: 'Get Products', refId: 'getProducts' },
      { id: 5, name: 'Update Product', refId: 'updateProduct' },
      { id: 6, name: 'Delete Product', refId: 'deleteProduct' }
   ],
   customer: [
      { id: 1, name: 'Create Customer', refId: 'createCustomer' },
      { id: 2, name: 'Create Customers', refId: 'createCustomers' },
      { id: 3, name: 'Get Customer', refId: 'getCustomer' },
      { id: 4, name: 'Get Customers', refId: 'getCustomers' },
      { id: 5, name: 'Update Customer', refId: 'updateCustomer' },
      { id: 6, name: 'Delete Customer', refId: 'deleteCustomer' }
   ]
};

const defaultResource: WooResource = 'product';
const defaultRequest = requestOptionsByResource[defaultResource][0].refId as string;

function getResourceFromRequest(request?: string): WooResource {
   if (!request) return defaultResource;
   for (const resource of Object.keys(requestOptionsByResource) as WooResource[]) {
      if (requestOptionsByResource[resource].some(option => option.refId === request)) {
         return resource;
      }
   }
   return defaultResource;
}

const functionDocs: { [key: string]: string } = {
   createCustomer: `Required:
- Payload JSON object
- email field in payload
Payload Example:
{
  "email": "alice@example.com",
  "first_name": "Alice",
  "last_name": "Johnson"
}`,
   createCustomers: `Required:
- Payload JSON array of customer objects
Payload Example:
[
  { "email": "alice@example.com", "first_name": "Alice" },
  { "email": "bob@example.com", "first_name": "Bob" }
]`,
   getCustomer: `Required:
- Customer ID
Optional Query:
{
  "context": "view"
}`,
   getCustomers: `Optional Query:
{
  "per_page": 50,
  "search": "alice"
}`,
   updateCustomer: `Required:
- Customer ID
- Payload JSON object`,
   deleteCustomer: `Required:
- Customer ID
Optional Force:
true`,
   createOrder: `Required:
- Payload JSON object
Payload Example:
{
  "payment_method": "cod",
  "set_paid": false,
  "line_items": [{ "product_id": 88, "quantity": 1 }]
}`,
   createOrders: `Required:
- Payload JSON array of order objects`,
   getOrder: `Required:
- Order ID
Optional Query:
{
  "context": "view"
}`,
   getOrders: `Optional Query:
{
  "per_page": 25,
  "status": "processing"
}`,
   updateOrder: `Required:
- Order ID
- Payload JSON object`,
   deleteOrder: `Required:
- Order ID
Optional Force:
true`,
   orderMarkAsPaid: `Order ID is required.
The backend updates the order with:
{
  "set_paid": true
}
If already paid, response remains success:true with a friendly message.`,
   returnCreate: `Order ID and Payload are required.
Payload Example:
{
  "amount": "10.00",
  "reason": "Customer return",
  "api_refund": true,
  "api_restock": true
}
Mapped to POST /orders/{orderId}/refunds.`,
   returnApproveRequest: `Order ID and Payload are required.
Payload Example:
{
  "amount": "10.00",
  "reason": "Return request approved"
}
Mapped to POST /orders/{orderId}/refunds.`,
   returnDeclineRequest: `Order ID and Return ID are required.
Optional Force:
true
Mapped to DELETE /orders/{orderId}/refunds/{returnId}.`,
   returnProcess: `Order ID and Payload are required.
Payload Example:
{
  "line_items": [
    { "id": 25, "quantity": 1, "refund_total": "10.00" }
  ],
  "api_restock": true
}
Mapped to POST /orders/{orderId}/refunds.`,
   refundCreate: `Order ID and Payload are required.
Payload Example:
{
  "amount": "10.00",
  "reason": "Approved refund",
  "api_refund": true
}
Mapped to POST /orders/{orderId}/refunds.`,
   inventoryRestockDecision: `Order ID and Payload are required.
Payload Example:
{
  "amount": "10.00",
  "line_items": [
    { "id": 25, "quantity": 1, "refund_total": "10.00" }
  ],
  "api_restock": true
}
Mapped to POST /orders/{orderId}/refunds.`,
   exchangeAction: `Order ID and Payload are required.
Payload Example:
{
  "amount": "0.00",
  "reason": "Exchange processed",
  "api_restock": true
}
Mapped to POST /orders/{orderId}/refunds.`,
   createProduct: `Required:
- Payload JSON object
Payload Example:
{
  "name": "Premium Hoodie",
  "type": "simple",
  "regular_price": "59.99"
}`,
   createProducts: `Required:
- Payload JSON array of product objects`,
   getProduct: `Required:
- Product ID`,
   getProducts: `Optional Query:
{
  "per_page": 50,
  "search": "hoodie"
}`,
   updateProduct: `Required:
- Product ID
- Payload JSON object`,
   deleteProduct: `Required:
- Product ID
Optional Force:
true`
};

export function WooCommerce({ id, data, setChildMinimize, setChildSettings }: WooCommerceProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            resource: defaultResource,
            request: defaultRequest,
            payload: '{\n  "name": "Sample Product"\n}',
            query: '{\n  "per_page": 50\n}',
            force: false
         });
      } else if (!data.value?.resource && data.value?.request) {
         updateNodeValue(id, setNodes, { resource: getResourceFromRequest(data.value.request) });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const activeRequestValue = data.value?.request as string | undefined;
   const storedResource = data.value?.resource;
   const activeResourceValue: WooResource = isWooResource(storedResource)
      ? storedResource
      : getResourceFromRequest(activeRequestValue);

   const requestOptionsForResource = requestOptionsByResource[activeResourceValue] ?? requestOptionsByResource[defaultResource];
   const activeRequest = requestOptionsForResource.some(request => request.refId === activeRequestValue)
      ? (activeRequestValue as string)
      : (requestOptionsForResource[0]?.refId as string);

   const resourceOptions = resourceOptionsBase.map(option => ({ ...option, isActive: option.refId === activeResourceValue }));
   const actionOptions = requestOptionsForResource.map(option => ({ ...option, isActive: option.refId === activeRequest }));

   const handleSelectResource = (selectId: number) => {
      const nextResource = resourceOptionsBase.find(resource => resource.id === selectId)?.refId as WooResource | undefined;
      if (!nextResource) return;
      const firstAction = requestOptionsByResource[nextResource][0]?.refId as string | undefined;
      updateNodeValue(id, setNodes, { resource: nextResource, request: firstAction });
   };

   const handleSelectAction = (selectId: number) => {
      const refId = requestOptionsForResource.find(request => request.id === selectId)?.refId;
      if (!refId) return;
      updateNodeValue(id, setNodes, { resource: activeResourceValue, request: refId });
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const isOrderIdRequest = [
      'getOrder',
      'updateOrder',
      'deleteOrder',
      'orderMarkAsPaid',
      'returnCreate',
      'returnApproveRequest',
      'returnDeclineRequest',
      'returnProcess',
      'refundCreate',
      'inventoryRestockDecision',
      'exchangeAction'
   ].includes(activeRequest);
   const isReturnIdRequest = ['returnDeclineRequest'].includes(activeRequest);
   const isCustomerIdRequest = ['getCustomer', 'updateCustomer', 'deleteCustomer'].includes(activeRequest);
   const isProductIdRequest = ['getProduct', 'updateProduct', 'deleteProduct'].includes(activeRequest);
   const isPayloadRequest = [
      'createOrder', 'createOrders', 'updateOrder',
      'createCustomer', 'createCustomers', 'updateCustomer',
      'createProduct', 'createProducts', 'updateProduct',
      'returnCreate', 'returnApproveRequest', 'returnProcess', 'refundCreate', 'inventoryRestockDecision', 'exchangeAction'
   ].includes(activeRequest);
   const isQueryRequest = [
      'getOrder', 'getOrders', 'getCustomer', 'getCustomers', 'getProduct', 'getProducts'
   ].includes(activeRequest);
   const isForceRequest = ['deleteOrder', 'deleteCustomer', 'deleteProduct', 'returnDeclineRequest'].includes(activeRequest);
   const isWarning = !data.value?.connection;

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={21} /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="woocommerce"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Resource" data={ resourceOptions } handleSelectItem={ handleSelectResource } isSmall={ true } />
                     <Select title="Action" data={ actionOptions } handleSelectItem={ handleSelectAction } isSmall={ true } />

                     {isOrderIdRequest ? (
                        <TextInput
                           keyRef="orderId"
                           value={ data.value?.orderId }
                           title="Order ID"
                           placeholder="723"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isReturnIdRequest ? (
                        <TextInput
                           keyRef="returnId"
                           value={ data.value?.returnId }
                           title="Return ID"
                           placeholder="726"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isCustomerIdRequest ? (
                        <TextInput
                           keyRef="customerId"
                           value={ data.value?.customerId }
                           title="Customer ID"
                           placeholder="123"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isProductIdRequest ? (
                        <TextInput
                           keyRef="productId"
                           value={ data.value?.productId }
                           title="Product ID"
                           placeholder="123"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isPayloadRequest ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={ activeRequest.includes('create') && activeRequest.includes('s')
                              ? '[\n  { "name": "Sample 1" },\n  { "name": "Sample 2" }\n]'
                              : '{\n  "name": "Sample"\n}' }
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryRequest ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={'{\n  "per_page": 50\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     {isForceRequest ? (
                        <TextInput
                           keyRef="force"
                           value={ String(data.value?.force ?? false) }
                           title="Force"
                           placeholder="true or false"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ functionDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}
